/**
 * Help Center Static Data (Issue #1348)
 *
 * Phase 1: Static TypeScript data
 * Phase 2: Migrate to API endpoint with admin editing
 *
 * Content guidelines:
 * - Action-oriented titles ("Create Your Club" not "Club Creation")
 * - Steps should be concise and scannable
 * - Tips for pro users, not essential info
 * - Duration = estimated read time
 */

import type { HelpArticle, HelpCategoryMeta } from '../types/help';

/**
 * Category metadata for index page
 */
export const HELP_CATEGORIES: HelpCategoryMeta[] = [
  {
    slug: 'getting-started',
    title: 'Getting Started',
    description: 'New to ORO move\u2122? Start here to set up your account and join your first club.',
    icon: 'Rocket',
  },
  {
    slug: 'clubs',
    title: 'Managing Clubs',
    description: 'Create and manage your club, invite members, and set up your team.',
    icon: 'Users',
  },
  {
    slug: 'events',
    title: 'Hosting Events',
    description: 'Create events, set up check-ins, and share with your community.',
    icon: 'Calendar',
  },
  {
    slug: 'attending',
    title: 'Attending Events',
    description: 'RSVP, check in, chat with your group, and sync to your calendar.',
    icon: 'MapPin',
  },
  {
    slug: 'account',
    title: 'Your Account',
    description: 'Manage your profile, notifications, and privacy settings.',
    icon: 'Settings',
  },
];

/**
 * All help articles
 *
 * Order matters for "next article" suggestions within categories.
 */
export const HELP_ARTICLES: HelpArticle[] = [
  // ============================================
  // GETTING STARTED
  // ============================================
  {
    slug: 'welcome',
    category: 'getting-started',
    title: 'Welcome to ORO move\u2122',
    description: 'What ORO move\u2122 is and why we built it differently.',
    duration: '2 min read',
    persona: 'new-user',
    steps: [
      {
        number: 1,
        title: 'Community over competition',
        content:
          'ORO move\u2122 helps running and cycling clubs organize group activities. We focus on showing up together, not racing or rankings.',
      },
      {
        number: 2,
        title: 'No algorithms, no engagement tricks',
        content:
          "We don't send push notifications to get you to open the app. We don't show feeds designed to keep you scrolling. ORO move\u2122 is a tool for real-world connection.",
      },
      {
        number: 3,
        title: 'Built for club leaders',
        content:
          'Create events, manage members, coordinate via chat, and share beautiful event cards. Everything a club organizer needs, nothing they don\'t.',
      },
    ],
    tips: [
      'ORO move\u2122 works best when your whole club is on it. Share your invite link!',
    ],
    nextArticle: 'sign-up',
  },
  {
    slug: 'sign-up',
    category: 'getting-started',
    title: 'Sign Up with Email or Phone',
    description: 'Create your account with just your email or phone number. No password needed.',
    duration: '2 min read',
    persona: 'new-user',
    isPublic: true, // Accessible without auth
    steps: [
      {
        number: 1,
        title: 'Enter your email or phone number',
        content:
          'Go to oro.fit and enter your email address or your mobile number. Either works, and there\'s no password to set. For email, we recommend the one your club already has for you.',
      },
      {
        number: 2,
        title: 'Check your inbox or your messages',
        content:
          'Enter an email and we send a magic link, with the subject "Your ORO move\u2122 login link". Check your spam folder too, just in case. Enter a phone number and we text you a six-digit code instead.',
      },
      {
        number: 3,
        title: 'Follow the link, or enter the code',
        content:
          'From the email, click the "Sign In to ORO move" button. From the text, type the six-digit code back into the app. The magic link lasts 15 minutes and the text code lasts 5, so use it soon.',
      },
      {
        number: 4,
        title: 'You\'re in!',
        content:
          'That\'s it. No password to remember, no account to verify. Next time you sign in, we\'ll send a fresh link or code the same way.',
      },
    ],
    tips: [
      'Magic links work on any device. Start on your phone, continue on desktop.',
      'If your link or code expires, just request a new one. It takes seconds.',
    ],
    nextArticle: 'complete-profile',
  },
  {
    slug: 'complete-profile',
    category: 'getting-started',
    title: 'Complete Your Profile',
    description: 'Set up your display name and profile photo so your club knows who you are.',
    duration: '2 min read',
    persona: 'new-user',
    steps: [
      {
        number: 1,
        title: 'Add your display name',
        content:
          'This is how you\'ll appear to other club members. Use your real name so people recognize you at events.',
      },
      {
        number: 2,
        title: 'Upload a profile photo',
        content:
          'A photo helps club leaders and members connect your face to your name. Action shots from events work great!',
      },
      {
        number: 3,
        title: 'Set your notification preferences',
        content:
          'Choose how you want to hear about events and messages. You can always change this later in Settings.',
      },
    ],
    tips: [
      'Your email is never shown to other members. Only club leaders can see it.',
    ],
    quickActions: [
      { label: 'Edit Your Profile', path: '/settings/profile' },
    ],
    nextArticle: 'join-club',
  },
  {
    slug: 'join-club',
    category: 'getting-started',
    title: 'Join Your First Club',
    description: 'Find and join a club using an invite link or by browsing public clubs.',
    duration: '2 min read',
    persona: 'new-user',
    steps: [
      {
        number: 1,
        title: 'Use an invite link',
        content:
          'The easiest way to join is through an invite link from someone in the club. Just click the link and you\'re in.',
      },
      {
        number: 2,
        title: 'Browse public clubs',
        content:
          'You can also browse public clubs in your area. Look for clubs that match your pace and schedule.',
      },
      {
        number: 3,
        title: 'Request to join private clubs',
        content:
          'Some clubs require approval. Submit a request and the club owner will review it.',
      },
    ],
    tips: [
      'You can be a member of multiple clubs. Join as many as you like!',
    ],
  },

  // ============================================
  // CLUBS (Leader-focused)
  // ============================================
  {
    slug: 'create-club',
    category: 'clubs',
    title: 'Create Your Club',
    description: 'Set up a new club on ORO move\u2122 for your running or cycling group.',
    duration: '3 min read',
    persona: 'leader',
    steps: [
      {
        number: 1,
        title: 'Tap "Create a Club"',
        content:
          'Open Settings and tap "Create a Club" in the Clubs section. You\'ll be the owner of this club.',
      },
      {
        number: 2,
        title: 'Name your club',
        content:
          'Choose a name that\'s easy to find and remember. Include your city or neighborhood if relevant.',
      },
      {
        number: 3,
        title: 'Add a description',
        content:
          'Tell potential members what your club is about. Mention pace, distance, meeting times, and vibe.',
      },
      {
        number: 4,
        title: 'Upload a cover photo',
        content:
          'A great photo of your group in action makes your club inviting. This appears on share cards too.',
      },
      {
        number: 5,
        title: 'Set visibility',
        content:
          'Choose Public (anyone can find and join), Invite-Only (visible but requires invite), or Private (hidden, invite only).',
      },
    ],
    tips: [
      'You can change all these settings later. Don\'t overthink it. Just get started.',
      'Invite-only works well for established groups who want some discoverability.',
    ],
    quickActions: [
      { label: 'Create a New Club', path: '/clubs/create' },
    ],
    nextArticle: 'club-settings',
  },
  {
    slug: 'club-settings',
    category: 'clubs',
    isPublic: true, // #2836: linked from the public Leader Getting Started guide
    title: 'Set Up Club Defaults',
    description: 'Save time by setting default location, distance, and check-in method.',
    duration: '2 min read',
    persona: 'leader',
    steps: [
      {
        number: 1,
        title: 'Go to Club Settings',
        content:
          'Open your club and tap the settings icon (gear) in the top right.',
      },
      {
        number: 2,
        title: 'Set default meeting location',
        content:
          'If your club usually meets at the same place, set it as the default. New events will pre-fill this location.',
      },
      {
        number: 3,
        title: 'Set default distance',
        content:
          'If your runs or rides are typically the same distance, set it as default.',
      },
      {
        number: 4,
        title: 'Choose default check-in method',
        content:
          'GPS, QR code, or secret word. GPS is easiest for most outdoor activities.',
      },
    ],
    tips: [
      'Defaults save time but can always be overridden per event.',
    ],
    quickActions: [
      { label: 'Open Club Settings', path: '/clubs/{slug}/settings', requiresClub: true },
    ],
    nextArticle: 'invite-members',
  },
  {
    slug: 'invite-members',
    category: 'clubs',
    isPublic: true, // #2836: linked from the public Leader Getting Started guide
    title: 'Invite Members',
    description: 'Create invite links to grow your club.',
    duration: '2 min read',
    persona: 'leader',
    steps: [
      {
        number: 1,
        title: 'Go to Invites',
        content:
          'From your club page, tap the "Invites" or menu icon to manage invitations.',
      },
      {
        number: 2,
        title: 'Create an invite link',
        content:
          'Generate a link that anyone can use to join your club.',
      },
      {
        number: 3,
        title: 'Choose single-use or reusable',
        content:
          'Single-use links expire after one person joins. Reusable links can be shared widely.',
      },
      {
        number: 4,
        title: 'Share the link',
        content:
          'Send via text, email, or post on social media. Anyone with the link can join.',
      },
    ],
    tips: [
      'Add a label to reusable links (e.g., "Instagram bio") to track where members come from.',
      'You can deactivate invite links anytime from the Invites page.',
    ],
    quickActions: [
      { label: 'Manage Invites', path: '/clubs/{slug}/settings/invites', requiresClub: true },
    ],
    nextArticle: 'manage-members',
  },
  {
    slug: 'manage-members',
    category: 'clubs',
    isPublic: true, // #2836: linked from the public Leader Getting Started guide
    title: 'Manage Your Team',
    description: 'Add leaders, review members, and handle the occasional removal.',
    duration: '3 min read',
    persona: 'leader',
    steps: [
      {
        number: 1,
        title: 'View member list',
        content:
          'From your club page, tap "Members" to see everyone in your club.',
      },
      {
        number: 2,
        title: 'Promote to Leader',
        content:
          'Tap a member and select "Make Leader." Leaders can create events and manage members.',
      },
      {
        number: 3,
        title: 'Review join requests',
        content:
          'For invite-only clubs, you\'ll see pending requests. Approve or decline each one.',
      },
      {
        number: 4,
        title: 'Remove a member',
        content:
          'If needed, you can remove someone from the club. They\'ll no longer see club content.',
      },
      {
        number: 5,
        title: 'Transfer ownership',
        content:
          'Moving on? Transfer club ownership to another leader from Club Settings.',
      },
    ],
    tips: [
      'Having 2-3 leaders helps share the load and ensures coverage.',
      'Leaders can see member emails for coordination; regular members cannot.',
    ],
    quickActions: [
      { label: 'View Members', path: '/clubs/{slug}/members', requiresClub: true },
    ],
  },

  // ============================================
  // HOSTING EVENTS (Leader-focused)
  // ============================================
  {
    slug: 'create-event',
    category: 'events',
    isPublic: true, // #2836: linked from the public Leader Getting Started guide
    title: 'Create Your First Event',
    description: 'Host a group run, ride, or walk and let your club know about it.',
    duration: '3 min read',
    persona: 'leader',
    steps: [
      {
        number: 1,
        title: 'Tap "Create Event"',
        content:
          'From your club page, tap "Create Event" to get started.',
      },
      {
        number: 2,
        title: 'Add a title and description',
        content:
          'Name your event and add any details members should know (pace, route, what to bring).',
      },
      {
        number: 3,
        title: 'Set date, time, and duration',
        content:
          'Set the start time and how long it runs so members can plan.',
      },
      {
        number: 4,
        title: 'Add location',
        content:
          'Where should people meet? You can use your club default or enter a new address.',
      },
      {
        number: 5,
        title: 'Choose check-in method',
        content:
          'How will you confirm attendance? GPS (members check in when nearby), QR code (scan at event), or secret word.',
      },
      {
        number: 6,
        title: 'Publish and share',
        content:
          'Tap "Create Event" to publish. Your club can see it and RSVP right away. Unless your club is private, you can share the event card anywhere.',
      },
    ],
    tips: [
      'Upload a cover photo to make your event card stand out when shared.',
      'Events with clear pace expectations get better turnout.',
    ],
    quickActions: [
      { label: 'Create an Event', path: '/clubs/{slug}/events/create', requiresClub: true },
    ],
    nextArticle: 'recurring-events',
  },
  {
    slug: 'recurring-events',
    category: 'events',
    title: 'Set Up Recurring Events',
    description: 'Create a weekly series so your club never misses a beat.',
    duration: '3 min read',
    persona: 'leader',
    steps: [
      {
        number: 1,
        title: 'Start creating an event',
        content:
          'Fill in your event details as usual: title, time, location, and check-in method.',
      },
      {
        number: 2,
        title: 'Tap "Create Series"',
        content:
          'Instead of creating a single event, tap "Create Series" to set up a repeating pattern.',
      },
      {
        number: 3,
        title: 'Choose your schedule',
        content:
          'Pick which days of the week and how often. You can run weekly on Tuesdays and Thursdays, for example.',
      },
      {
        number: 4,
        title: 'Set an end date',
        content:
          'Choose a number of occurrences (4, 6, 8, 10, or 12), a specific end date, or let it run up to a year.',
      },
      {
        number: 5,
        title: 'All events are created at once',
        content:
          'Your series generates individual events that members can RSVP to and check in to separately.',
      },
    ],
    tips: [
      'Each event in a series gets its own chat room, which is great for day-of coordination.',
      'Need to change something? Edit a single event or update all future events at once.',
    ],
    nextArticle: 'virtual-events',
  },
  {
    slug: 'virtual-events',
    category: 'events',
    title: 'Host a Virtual Event',
    description: 'Bring your club together online with virtual rides and runs.',
    duration: '2 min read',
    persona: 'leader',
    steps: [
      {
        number: 1,
        title: 'Select "Virtual" mode',
        content:
          'When creating or editing an event, toggle the meeting mode from "In-Person" to "Virtual."',
      },
      {
        number: 2,
        title: 'Choose your platform',
        content:
          'Pick from Zwift, Rouvy, MyWhoosh, TrainingPeaks Virtual, or enter a custom platform name.',
      },
      {
        number: 3,
        title: 'Add a leader username',
        content:
          'Optionally add your platform username so members can find and join your session.',
      },
      {
        number: 4,
        title: 'Check-in uses a secret word',
        content:
          'Virtual events automatically use secret word check-in. Share the word with your group at ride time.',
      },
    ],
    tips: [
      'Virtual events skip the location fields. No address needed.',
      'Great for rainy days or connecting with members who can\'t make it in person.',
    ],
    nextArticle: 'checkin-methods',
  },
  {
    slug: 'checkin-methods',
    category: 'events',
    isPublic: true, // #2836: linked from the public Leader Getting Started guide
    title: 'Choose a Check-In Method',
    description: 'Track attendance with GPS, QR codes, or a secret word.',
    duration: '3 min read',
    persona: 'leader',
    steps: [
      {
        number: 1,
        title: 'Enable check-ins',
        content:
          'In the event form, toggle check-ins on. This lets members confirm they showed up.',
      },
      {
        number: 2,
        title: 'GPS check-in',
        content:
          'Members tap "Check In" when they\'re near the meeting spot. You can set the radius from 50 to 500 meters.',
      },
      {
        number: 3,
        title: 'QR code check-in',
        content:
          'Display a QR code at the event. Members scan it with their phone to check in.',
      },
      {
        number: 4,
        title: 'Secret word check-in',
        content:
          'Announce a word at the event. Members type it in to confirm attendance. Required for virtual events.',
      },
      {
        number: 5,
        title: 'Manual check-in',
        content:
          'Leaders check in members themselves from the attendance list. Good for small groups or when members don\'t have their phones handy.',
      },
      {
        number: 6,
        title: 'Set the check-in window',
        content:
          'Members can check in starting 15 minutes before the event and up to 15 minutes after. You can extend this to 30, 60, or 120 minutes.',
      },
    ],
    tips: [
      'GPS works best for outdoor meetups. Secret word is ideal for virtual events or indoor sessions.',
      'Leaders can always check in members manually if someone has trouble.',
    ],
    nextArticle: 'event-sharing',
  },
  {
    slug: 'event-sharing',
    category: 'events',
    title: 'Share Your Event',
    description: 'Get the word out with shareable event cards and links.',
    duration: '2 min read',
    persona: 'leader',
    steps: [
      {
        number: 1,
        title: 'Open the share menu',
        content:
          'From any event page, tap "Share" to see your sharing options.',
      },
      {
        number: 2,
        title: 'Choose a card style',
        content:
          'Pick from your event cover photo, route map, club image, or a default design.',
      },
      {
        number: 3,
        title: 'Copy the link or share directly',
        content:
          'Copy the event link to your clipboard, or share directly to social media and messaging apps.',
      },
      {
        number: 4,
        title: 'Download the card',
        content:
          'Save the share card as an image to post on Instagram, WhatsApp, or anywhere else.',
      },
    ],
    tips: [
      'Events with cover photos make more eye-catching share cards.',
      'Upload a GPX route to get a route map option on your share card.',
    ],
    nextArticle: 'pace-groups',
  },
  {
    slug: 'pace-groups',
    category: 'events',
    title: 'Set Up Pace Groups',
    description: 'Help attendees find their pace by creating speed-based groups.',
    duration: '3 min read',
    persona: 'leader',
    steps: [
      {
        number: 1,
        title: 'Edit your event',
        content:
          'Open an event and tap Edit. Scroll to the Pace Groups section.',
      },
      {
        number: 2,
        title: 'Add pace groups',
        content:
          'Create groups like "Fast (sub-8 min/mi)", "Medium (8-10 min/mi)", "Social (10+ min/mi)".',
      },
      {
        number: 3,
        title: 'Set descriptions',
        content:
          'Add context like "No-drop, we regroup at mile markers" to set expectations.',
      },
      {
        number: 4,
        title: 'Members choose when RSVPing',
        content:
          'When members RSVP, they pick their pace group. You\'ll see who\'s in each group.',
      },
    ],
    tips: [
      'Pace groups reduce anxiety for newer runners who worry about keeping up.',
      'Consider a "Social" or "No-drop" group for maximum inclusivity.',
    ],
  },

  // ============================================
  // ATTENDING (Member-focused)
  // ============================================
  {
    slug: 'rsvp',
    category: 'attending',
    title: 'RSVP to Events',
    description: 'Let your club know if you\'re coming to an event.',
    duration: '2 min read',
    persona: 'member',
    steps: [
      {
        number: 1,
        title: 'Find the event',
        content:
          'Open your club and look for upcoming events. Tap on one to see details.',
      },
      {
        number: 2,
        title: 'Tap your response',
        content:
          'Choose Yes, Maybe, or No. Your response helps organizers plan.',
      },
      {
        number: 3,
        title: 'Select a pace group',
        content:
          'If the event has pace groups, pick the one that fits you best.',
      },
    ],
    tips: [
      'You can change your RSVP anytime before the event starts.',
      'Even a "Maybe" helps organizers know there\'s interest.',
    ],
    nextArticle: 'check-in',
  },
  {
    slug: 'check-in',
    category: 'attending',
    title: 'Check In at Events',
    description: 'Confirm your attendance when you arrive at an event.',
    duration: '2 min read',
    persona: 'member',
    steps: [
      {
        number: 1,
        title: 'Open the event',
        content:
          'On event day, open the event page in ORO move\u2122. You\'ll see a "Check In" button.',
      },
      {
        number: 2,
        title: 'GPS check-in',
        content:
          'If the event uses GPS, just tap "Check In" when you\'re at the meeting spot. ORO move\u2122 confirms your location.',
      },
      {
        number: 3,
        title: 'QR code check-in',
        content:
          'For QR events, scan the code displayed by the organizer with your phone camera.',
      },
      {
        number: 4,
        title: 'Secret word check-in',
        content:
          'Ask the organizer for the secret word and enter it in the app.',
      },
    ],
    tips: [
      'Check-in is how clubs track who actually showed up, not just who RSVP\'d.',
      'Having trouble with GPS? Make sure location services are enabled for your browser.',
    ],
    nextArticle: 'event-chat',
  },
  {
    slug: 'event-chat',
    category: 'attending',
    title: 'Join Event Chat',
    description: 'Coordinate with your group before, during, and after the event.',
    duration: '2 min read',
    persona: 'member',
    steps: [
      {
        number: 1,
        title: 'Open event chat',
        content:
          'From the event page, tap "Chat" or "Discuss" to open the event chat room.',
      },
      {
        number: 2,
        title: 'Post updates',
        content:
          'Share info like "Running 5 mins late" or "I\'m by the water fountain."',
      },
      {
        number: 3,
        title: 'Share photos',
        content:
          'After the event, share photos with the group. Great way to celebrate showing up!',
      },
    ],
    tips: [
      'Event chats stay available after the event ends, so post your recap!',
      'For recurring events, each instance has its own chat.',
    ],
    nextArticle: 'calendar-sync',
  },
  {
    slug: 'calendar-sync',
    category: 'attending',
    title: 'Add Events to Calendar',
    description: 'Get ORO move\u2122 events on your phone\'s calendar so you never miss one.',
    duration: '2 min read',
    persona: 'member',
    steps: [
      {
        number: 1,
        title: 'Add a single event',
        content:
          'On any event page, tap "Add to Calendar" to download an .ics file for that event.',
      },
      {
        number: 2,
        title: 'Open the file',
        content:
          'Your phone will prompt you to add it to your calendar app (Google, Apple, Outlook, etc.).',
      },
      {
        number: 3,
        title: 'Subscribe to club calendar',
        content:
          'For automatic updates, subscribe to your club\'s calendar feed. New events appear automatically.',
      },
      {
        number: 4,
        title: 'Find the subscribe link',
        content:
          'Go to your club, tap the calendar icon, and copy the subscription URL. Paste it into your calendar app.',
      },
    ],
    tips: [
      'Calendar subscriptions update automatically, so you\'ll see new events as they\'re created.',
      'You can filter to only show events you\'ve RSVP\'d to.',
    ],
    quickActions: [
      { label: 'Calendar Settings', path: '/settings/calendar' },
    ],
  },

  // ============================================
  // ACCOUNT
  // ============================================
  {
    slug: 'edit-profile',
    category: 'account',
    title: 'Edit Your Profile',
    description: 'Update your name, photo, and other profile details.',
    duration: '2 min read',
    persona: 'member',
    steps: [
      {
        number: 1,
        title: 'Go to Settings',
        content:
          'Tap your profile icon or the menu, then select "Settings."',
      },
      {
        number: 2,
        title: 'Tap "Edit Profile"',
        content:
          'Update your display name, profile photo, or bio.',
      },
      {
        number: 3,
        title: 'Save changes',
        content:
          'Tap "Save" when you\'re done. Changes appear immediately to other members.',
      },
    ],
    quickActions: [
      { label: 'Edit Your Profile', path: '/settings/profile' },
    ],
    nextArticle: 'notifications',
  },
  {
    slug: 'notifications',
    category: 'account',
    title: 'Manage Notifications',
    description: 'Control when and how ORO move\u2122 contacts you.',
    duration: '2 min read',
    persona: 'member',
    steps: [
      {
        number: 1,
        title: 'Go to Settings',
        content:
          'Tap your profile icon, then "Settings," then "Notifications."',
      },
      {
        number: 2,
        title: 'Choose notification types',
        content:
          'Toggle on/off: new events, event reminders, chat messages, club announcements.',
      },
      {
        number: 3,
        title: 'Choose channels',
        content:
          'Get notifications via email, SMS (if enabled), or both.',
      },
    ],
    tips: [
      'We send event reminders 24 hours before. You can\'t miss one you\'ve RSVP\'d to.',
      'We never send marketing emails. Only stuff you opted into.',
    ],
    quickActions: [
      { label: 'Notification Settings', path: '/settings/notifications' },
    ],
  },
];
